// Libs
import axios, { AxiosResponse } from 'axios';
import { SWRResponse } from 'swr';
// App
import { CATALOG_SHOPS_URL } from './shop.constants';
import { CategoryDetails } from 'app-shared/lib/api/shared';
import { useSWRAxiosGetRequest } from 'app-shared/lib/api/utils/swr-axios-request.hooks';
import { ShopDetails, ShopSummary } from './shop.interfaces';
import { AxiosHookResponse, useAxiosGetRequest } from 'app-shared/lib/api/utils/axios-request.hooks';
import { SearchHookResponse } from 'app-shared/lib/api/utils/search-hook.interfaces';
import useSearch from 'app-shared/lib/api/utils/search-hook';
import { escapeApostrophes } from 'app-shared/lib/utils/search.utils';
import { API_COMMON_BASE_URL } from 'app-shared/lib/api/utils/api.constants';

/**
 * Get shop api
 */
export const getShopById = (id: number): Promise<AxiosResponse<ShopDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}`;

  return axios.get(url);
};

/**
 * Get shop by slug api
 */
export const getShopBySlug = (slug: string): Promise<AxiosResponse<ShopDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}?slug=${slug}`;

  return axios.get(url);
};

/**
 * Get shop api hook
 */
export const useGetShop = (id: number): SWRResponse<ShopDetails, Error> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}` : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get shops api
 */
export const getShops = (page = 0, size = 20): Promise<AxiosResponse<ShopSummary[]>> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/summaries?page=${page}&size=${size}`;

  return axios.get(url);
};

/**
 * Get shops api hook
 */
export const useGetShops = (): AxiosHookResponse<void, ShopSummary[]> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/summaries`;

  return useAxiosGetRequest(url);
};

/**
 * Search shops api axios fetcher
 * @param keywords the keywords for search
 */
export const searchShopsFetcher = (
  keywords: string,
  mainCategoryId?: number,
  shopCategoryId?: number
): Promise<AxiosResponse> => {
  const mainCategoryIdQueryPart = (mainCategoryId && `&mainCategoryId=${mainCategoryId}`) || '';
  const shopCategoryIdQueryPart = (shopCategoryId && `&shopCategoryId=${shopCategoryId}`) || '';
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/search?keywords=${escapeApostrophes(
    keywords
  )}${mainCategoryIdQueryPart}${shopCategoryIdQueryPart}`;

  return axios.get(url);
};

export const useSearchShops = (): SearchHookResponse<ShopSummary> => {
  return useSearch(searchShopsFetcher);
};

/**
 * Get shop main categories api hook
 */
export const useGetShopMainCategories = (id: number): SWRResponse<CategoryDetails[], Error> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/main-categories` : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Build shop product categories api url, also used as SWR cache key
 */
export const buildShopProductCategoriesUrl = (id: number): string => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/product-categories`;

  return url;
};

/**
 * Get shop product categories api hook
 */
export const useGetShopProductCategoriesWithoutCaching = (id: number): AxiosHookResponse<void, CategoryDetails[]> => {
  const urlOptional = (id && buildShopProductCategoriesUrl(id)) || null;

  return useAxiosGetRequest(urlOptional);
};

/**
 * Get shop product categories api
 */
export const getShopProductCategories = (id: number): Promise<AxiosResponse<CategoryDetails[]>> => {
  const url = buildShopProductCategoriesUrl(id);

  return axios.get(url);
};

/**
 * Get shop product categories api hook
 */
export const useGetShopProductCategories = (id: number): SWRResponse<CategoryDetails[], Error> => {
  const urlOptional = (id && buildShopProductCategoriesUrl(id)) || null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};
